import { useCallback } from 'react';
import { useIntl } from 'react-intl';

import type { SelectInputProps } from '@/modules/forms';
import { useGroups } from '@/modules/groups/hooks/useGroups';
import { createGroupLabel } from '@/modules/groups/utilities';
import { useWhitelistUsers } from '@/modules/whitelist';

import { VAULTS_FILTER_ALL_VALUE } from '../../../constants';
import type { VaultsTableFilters } from '../../../types';

type VaultsFilterSelectProps = Partial<
    Pick<SelectInputProps, 'options' | 'loadMore' | 'loading' | 'loadingMore' | 'hasMore' | 'showSearch' | 'onBlur' | 'onSelect'>
>;

/**
 * The options cannot be collected from the rows on screen: the table shows one server-side page, so a
 * group or user whose vaults sit on another page would silently drop out of its own filter.
 */
export const useVaultsFilterSelectProps = () => {
    const { formatMessage } = useIntl();

    const allOption = { value: VAULTS_FILTER_ALL_VALUE, label: formatMessage({ id: 'vaults.filters.value.all' }) };

    const { api: groupsApi, dataWithoutAllUsersGroup: groups, handleLoadMore: loadMoreGroups, handleSearch: searchGroups } = useGroups();
    const { api: usersApi, data: users, handleLoadMore: loadMoreUsers, handleSearch: searchUsers } = useWhitelistUsers();

    const resetGroupsSearch = useCallback(() => {
        searchGroups('');
    }, [searchGroups]);

    const resetUsersSearch = useCallback(() => {
        searchUsers('');
    }, [searchUsers]);

    return {
        groups: {
            options: [allOption, ...groups.map(group => ({ value: group.id, label: createGroupLabel(group) }))],
            loading: groupsApi.inProgress,
            loadingMore: groupsApi.inProgressNext,
            hasMore: Boolean(groupsApi.nextPageToken),
            loadMore: loadMoreGroups,
            showSearch: { filterOption: false, autoClearSearchValue: true, onSearch: searchGroups },
            onBlur: resetGroupsSearch,
            onSelect: resetGroupsSearch,
        },
        users: {
            options: [allOption, ...users.map(user => ({ value: user.id, label: user.name || user.email }))],
            loading: usersApi.inProgress,
            loadingMore: usersApi.inProgressNext,
            hasMore: Boolean(usersApi.nextPageToken),
            loadMore: loadMoreUsers,
            showSearch: { filterOption: false, autoClearSearchValue: true, onSearch: searchUsers },
            onBlur: resetUsersSearch,
            onSelect: resetUsersSearch,
        },
    } as const satisfies Record<Exclude<keyof VaultsTableFilters, 'search'>, VaultsFilterSelectProps>;
};
